"use client";

import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  Facebook,
  Video,
  FileText,
  Monitor,
  PenTool,
  Twitter,
  Users,
  Mail,
  BookOpen,
  HelpCircle,
  Linkedin,
  Package,
  MessageCircle,
  Instagram,
  Send,
} from "lucide-react";

const tasks = [
  {
    title: "Social Ads",
    icon: <Users className="w-4 h-4" />,
    bgColor: "bg-blue-500",
  },
  {
    title: "Facebook Posts",
    icon: <Facebook className="w-4 h-4" />,
    bgColor: "bg-blue-600",
  },
  {
    title: "Video Scripts",
    icon: <Video className="w-4 h-4" />,
    bgColor: "bg-red-500",
  },
  {
    title: "Press Releases",
    icon: <FileText className="w-4 h-4" />,
    bgColor: "bg-green-500",
  },
  {
    title: "Google Display Ads",
    icon: <Monitor className="w-4 h-4" />,
    bgColor: "bg-yellow-500",
  },
  {
    title: "Blog Posts",
    icon: <PenTool className="w-4 h-4" />,
    bgColor: "bg-purple-500",
  },
  {
    title: "X/Twitter Thread",
    icon: <Twitter className="w-4 h-4" />,
    bgColor: "bg-black",
  },
  {
    title: "Thought Leadership Articles",
    icon: <BookOpen className="w-4 h-4" />,
    bgColor: "bg-indigo-500",
  },
  {
    title: "Email Newsletter Articles",
    icon: <Mail className="w-4 h-4" />,
    bgColor: "bg-pink-500",
  },
  {
    title: "Whitepaper Reports",
    icon: <FileText className="w-4 h-4" />,
    bgColor: "bg-gray-500",
  },
  {
    title: "How-To Guides & Tutorials",
    icon: <HelpCircle className="w-4 h-4" />,
    bgColor: "bg-orange-500",
  },
  {
    title: "LinkedIn Thought Leadership Posts",
    icon: <Linkedin className="w-4 h-4" />,
    bgColor: "bg-blue-700",
  },
  {
    title: "Product Descriptions",
    icon: <Package className="w-4 h-4" />,
    bgColor: "bg-teal-500",
  },
  {
    title: "Elevator Pitch",
    icon: <MessageCircle className="w-4 h-4" />,
    bgColor: "bg-cyan-500",
  },

  {
    title: "Promotional Emails",
    icon: <Mail className="w-4 h-4" />,
    bgColor: "bg-rose-500",
  },
  {
    title: "Instagram Posts",
    icon: <Instagram className="w-4 h-4" />,
    bgColor: "bg-gradient-to-r from-purple-500 to-pink-500",
  },
  {
    title: "Media Pitch Emails",
    icon: <Send className="w-4 h-4" />,
    bgColor: "bg-violet-500",
  },
];

export default function FeatureSection() {
  return (
    <section className="relative w-full py-20 px-4 bg-slate-50/30 dark:bg-slate-900/20 text-foreground">
      <div className="mx-auto max-w-5xl grid grid-cols-1 md:grid-cols-2 items-center justify-items-center gap-12 text-center md:text-left">
        {/* LEFT SIDE - Task Loop with Vertical Bar */}
        <div className="relative w-full max-w-sm">
          <Card className="overflow-hidden bg-muted/30 dark:bg-muted/20 backdrop-blur-md shadow-xl rounded-lg">
            <CardContent className="relative h-[320px] p-0 overflow-hidden">
              {/* Scrollable Container */}
              <div className="relative h-full overflow-hidden">
                {/* Motion list */}
                <motion.div
                  className="flex flex-col gap-2 absolute w-full"
                  animate={{ y: ["0%", "-50%"] }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 14,
                    ease: "linear",
                  }}
                >
                  {[...tasks, ...tasks].map((task, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 relative"
                    >
                      {/* Icon + Content */}
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`w-8 h-8 rounded-full ${task.bgColor} text-white shadow-md flex items-center justify-center`}>
                          {task.icon}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{task.title}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>

                {/* Fade effect only inside card */}
                <div className="absolute top-0 left-0 w-full h-12 bg-gradient-to-b from-background via-background/70 to-transparent pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-background via-background/70 to-transparent pointer-events-none" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT SIDE - Content */}
        <div className="space-y-6">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-relaxed">
            Your Marketing Playground
          </h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed">
            Dream it, brief it, ship it. Studio turns every idea—big or small—into polished, on-brand campaigns, posts, emails, scripts, and more. No channel left behind, no creativity capped.
          </p>

          <div className="flex gap-3 flex-wrap justify-center md:justify-start">
            <Badge className="px-4 py-2 text-sm">All Channels</Badge>
            <Badge className="px-4 py-2 text-sm">On-Brand</Badge>
            <Badge className="px-4 py-2 text-sm">Creative Freedom</Badge>
          </div>
        </div>
      </div>
    </section>
  );
}